import * as connectionService from 'services/connection-status-service';
import * as logger from 'logger';
import * as tenantService from 'services/tenant-service';
import * as userApi from 'api/user-api';
import * as userService from 'services/user-service';
import { Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { Guid } from 'types/guid';
import { ScanAction, USER_BARCODE_SCAN_ACTION } from 'components/scanner/actions';
import { translations } from 'types/translations';
import {
  GET_USER,
  GetUserAction,
  LoadUserAction,
  ProcessUserAction,
  RESET_PASSWORD_ACTION,
  ResetPasswordAction,
  ResetPasswordCompleteAction,
  ResetPasswordErrorAction,
  SAVE_USER,
  SaveUserAction,
  ScanUserBarcodeAction,
  SetUserAction,
  UserManagementErrorAction,
  ACTIVATE_USER,
  DEACTIVATE_USER,
  SetCreateUserStatusAction,
  SET_USER
} from './actions';

const UserManagementMiddleware: Middleware = store => next => async action => {

  try {
    switch (action.type) {

      case SET_USER:
        await setUser(store, action);
        break;

      case GET_USER:
        try {
          await getUser(store, action);
        } catch (error) {
          logger.error(error, 'UsersMiddleware.GetUser');
          store.dispatch(new UserManagementErrorAction(error.message));
        }
        break;

      case SAVE_USER:
        try {
          await saveUser(store, action);
        }
        catch (error) {
          logger.error(error, 'UsersMiddleware.SaveUser');
          store.dispatch(new UserManagementErrorAction(error.message));
        }
        break;

      case USER_BARCODE_SCAN_ACTION:
        await processScan(store, next, action);
        break;

      case RESET_PASSWORD_ACTION:
        await resetPassword(store, action);
        break;

      case ACTIVATE_USER:
        await activateUser(store, action);
        break;

      case DEACTIVATE_USER:
        await deactivateUser(store, action);
        break;

      default:
        break;
    }

    next(JSON.parse(JSON.stringify(action)));
  }
  catch (e) {
    const error = e as Error;
    logger.error(error, 'UsersMiddleware');
  }
};

async function getUser(store: MiddlewareAPI, action: GetUserAction): Promise<void> {
  store.dispatch(new LoadUserAction());
  const tenantId = await tenantService.getTenantId();
  const user = await userApi.getUser(tenantId, action.userId.toString());
  store.dispatch(new SetUserAction(user));
}

async function saveUser(store: MiddlewareAPI, action: SaveUserAction): Promise<void> {
  store.dispatch(new ProcessUserAction());
  const tenantId = await tenantService.getTenantId();
  if (!action.user.id) {
    action.user.id = Guid.empty();
    await userApi.createUser(tenantId, action.user);
    store.dispatch(new SetCreateUserStatusAction(true));
  } else {
    var user = await userApi.updateUser(tenantId, action.user.id, action.user);
    store.dispatch(new SetUserAction(user));
  }
}

async function activateUser(store: MiddlewareAPI, action: GetUserAction): Promise<void> {
  store.dispatch(new ProcessUserAction());
  const tenantId = await tenantService.getTenantId();
  const user = await userApi.activateUser(tenantId, action.userId.toString());
  store.dispatch(new SetUserAction(user));
}

async function deactivateUser(store: MiddlewareAPI, action: GetUserAction): Promise<void> {
  store.dispatch(new ProcessUserAction());
  const tenantId = await tenantService.getTenantId();
  const user = await userApi.deactivateUser(tenantId, action.userId.toString());
  store.dispatch(new SetUserAction(user));
}

async function resetPassword(store: MiddlewareAPI, action: ResetPasswordAction): Promise<void> {
  store.dispatch(new ProcessUserAction());
  const tenantId = await tenantService.getTenantId();
  const isOnline = await connectionService.getStatus();
  try {
    await userApi.resetPassword(tenantId, action.userEmail);
    store.dispatch(new ResetPasswordCompleteAction());
  }
  catch (e) {
    const error = e as Error;
    logger.error(error, 'LoginMiddleware.login');
    if (isOnline) {
      store.dispatch(new ResetPasswordErrorAction(e.message));
    } else {
      store.dispatch(new ResetPasswordErrorAction(translations.PasswordResetOfflineError));
    }
  }
}

async function setUser(store: MiddlewareAPI, action: SetUserAction): Promise<void> {
  await userService.saveUser(action.user);
}

function processScan(store: MiddlewareAPI, next: Dispatch, action: ScanAction): void {
  if (action.epc) {
    store.dispatch(new ScanUserBarcodeAction(action.epc));
  }
}

export default UserManagementMiddleware;


// WEBPACK FOOTER //
// ./src/pages/user-management/middleware.ts